import { NegotiationEventRequest } from '@interfaces/models/negotiationEventRequest';
import { NegotiationRequest } from '@interfaces/models/negotiationRequest';
import { Price } from '@interfaces/models/price';
import { Product } from '@interfaces/models/product';
import { Negotiation } from '@interfaces/models/negotiation';
import NegotiationsService from '@services/negotiations-service';
import ProductService from '@services/product-service';

export default class MmaoService {
  static MAX_DISCOUNT: number = 0.7;
  // public hideNotifications = new Subject<boolean>();

  /**
   * Returns the minimum price for an offer based on current price.
   * @param currentPrice the starting negotiation price.
   * @returns the minimum allowed offer, in whole number
   */
  static getMinOffer(currentPrice: Price): number {
    return currentPrice.cents ? Math.ceil((currentPrice.cents * MmaoService.MAX_DISCOUNT) / 100) : 0;
  }

  /**
   * Returns the maximum price for an offer based on current price.
   * @param currentPrice the starting negotiation price.
   * @returns the maximum allowed offer, in whole number
   */
  static getMaxOffer(currentPrice: Price): number {
    return currentPrice.cents ? Math.round(currentPrice.cents / 100) : 0;
  }

  static async createNegotiation(
    product: Product,
    price: number,
    buyerId?: Negotiation['buyer']['id'],
  ): Promise<Negotiation> {
    const request: NegotiationRequest = {
      offerInWholeNumber: price,
    };

    if (buyerId) {
      request.buyerId = buyerId;
    }

    try {
      return await ProductService.createNegotiation(product.id, request);
    } catch (e) {
      throw e?.response?.data?.errors?.detail || e;
    }
  }

  static addEvent = async (
    negotiationId: Negotiation['id'],
    action: NegotiationEventRequest['action'],
    price?: number,
  ): Promise<Negotiation> => {
    const event: NegotiationEventRequest = {
      action,
    };

    if (action === 'offer') {
      event.offerInWholeNumber = price;
    }

    const { data } = await NegotiationsService.addNegotiationEvent(negotiationId, event);
    return data;
  };

  static async sendOffer(negotiation: Negotiation, price: number): Promise<Negotiation> {
    return this.addEvent(negotiation.id, 'offer', price);
  }

  static async acceptOffer(negotiation: Negotiation): Promise<Negotiation> {
    return this.addEvent(negotiation.id, 'accept');
  }

  static async rejectOffer(negotiation: Negotiation): Promise<Negotiation> {
    return this.addEvent(negotiation.id, 'reject');
  }

  /**
   * Here are the rules for product that are available to negotiations.
   *   - product MUST be on sale,
   *   - product MUST NOT be reserved by,
   *   - product MUST NOT be in a process of purchase,
   *   - product MUST NOT be in a fraud screening process,
   *   - product MUST NOT be prohibited for shipping,
   *   - product MUST NOT already be in a negotiation process by buyer,
   *   - seller MUST have enabled negotiation option for this product.
   */
  static canMakeAnOffer(product: Product, negotiation: Negotiation): boolean {
    return this.mmaoCheck(product) && (!negotiation || !this.hasPendingNegotiations(negotiation));
  }

  static canSellerMakeAnOffer(product: Product): boolean {
    return this.mmaoCheck(product);
  }

  static mmaoCheck(product: Product): boolean {
    return !product.sold && !product.reserved && product.negotiable;
  }

  static hasPendingNegotiations(negotiation: Negotiation): boolean {
    if (!negotiation) {
      return false;
    }
    const expirationDate = new Date(negotiation.expirationDate);
    return !negotiation.isClosed && expirationDate > new Date();
  }

  static async getNegotiationsByProduct(productId: Product['id']): Promise<Negotiation[]> {
    return NegotiationsService.listNegotiations('seller', true)
      .then((res) => res.data.filter((n: Negotiation) => n.product.id === productId && !n.isClosed))
      .catch((err) => {
        throw err?.response?.data?.errors?.detail || err;
      });
  }
}
